import { useEffect, useState } from 'react';
import { GET_SKU_URL } from '@constants';
import { useAuthHttp } from '@hooks';
import { SelectedSKU } from '@forms/SKUForm/types';
import { InventorySKUListing } from '@views/StockBySKU/components/types';

type FetchedSku = SelectedSKU['sku'];

const toNumber = (value: unknown): number => Number(value) || 0;

const temperatureOf = (
  temperature: { quantity: string | number; unitOfMeasure: string } | null
) =>
  temperature
    ? {
        quantity: toNumber(temperature.quantity),
        unitOfMeasure: temperature.unitOfMeasure,
      }
    : null;

export const toInventorySkuListing = (
  fetched: FetchedSku
): InventorySKUListing =>
  ({
    ...fetched,
    id: fetched.inventorySkuId,
    productId: fetched.productDetails?.productId ?? '',
    givenProductId: fetched.productDetails?.productId ?? '',
    productName: fetched.productDetails?.name ?? null,
    brand: fetched.productDetails?.brand ?? null,
    productCategory: fetched.productDetails?.category ?? null,
    productSubCategory: fetched.productDetails?.subCategory ?? null,
    images: fetched.images ?? [],
    productImages: [],
    metaData: fetched.metaData ?? [],
    productMetaData: [],
    costPrices: fetched.costPrices ?? [],
    listPrices: fetched.listPrices ?? [],
    palletising: fetched.palletising ?? [],
    stockByWarehouse: fetched.stockByWarehouse ?? [],
    shipmentNotes: fetched.shipmentNotes ?? [],
    temperatureControlRequirements: fetched.temperatureControlRequirements
      ? {
          temperatureControlType:
            fetched.temperatureControlRequirements.temperatureControlType,
          minimumTemperature: temperatureOf(
            fetched.temperatureControlRequirements.minimumTemperature
          ),
          maximumTemperature: temperatureOf(
            fetched.temperatureControlRequirements.maximumTemperature
          ),
        }
      : null,
  } as unknown as InventorySKUListing);

export function useSkuDetails(
  inventorySkuId: string | null | undefined,
  isEnabled: boolean
) {
  const authHttp = useAuthHttp();
  const [sku, setSku] = useState<InventorySKUListing | null>(null);
  const [isLoading, setIsLoading] = useState(isEnabled);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    if (!isEnabled || !inventorySkuId) return undefined;

    let isCurrentRequest = true;
    setIsLoading(true);
    setHasFailed(false);

    authHttp
      .get<SelectedSKU>(GET_SKU_URL, { params: { skuId: inventorySkuId } })
      .then(({ data }) => {
        if (!isCurrentRequest) return;
        if (data?.sku) setSku(toInventorySkuListing(data.sku));
        else setHasFailed(true);
      })
      .catch(() => {
        if (isCurrentRequest) setHasFailed(true);
      })
      .finally(() => {
        if (isCurrentRequest) setIsLoading(false);
      });

    return () => {
      isCurrentRequest = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventorySkuId, isEnabled]);

  return { sku, isLoading, hasFailed };
}
